import { defineStore } from 'pinia'
import * as apiAuthentication from '@/network/api/authentication.api.js'
import * as apiDashboardCard from '@/network/api/dashboardcard.api.js'
import * as apiPopup from '@/network/api/popup.api.js'
import * as apiAccounts from '@/network/api/accounts.api.js'
import * as apiCustomers from '@/network/api/customers/customers.api.js'
import * as apiMerchant from '@/network/api/mobilePayments.api.js'
import { ACCOUNT_GROUPS_COLLORS } from '@/assets/constants/core'
import { firstLastWordBuilder, acronymBuilder } from '@/assets/scripts/strings'
import { parseTimer } from '@/assets/scripts/dateBuilders'
import { addItemToSessionStorage, removeItemFromSessionStorage } from '@/assets/scripts/windowStorage'
import { USER_SESSION } from '@/assets/constants/core'
import i18n from '@/assets/setups/i18n'

export const useSessionStore = defineStore('sessionStore', {
    state: () => ({
        currentAppThemeClientType: 'particular',
        allUserInfo: null,
        sessionExpired: false,
        userSession: null,
        outOfBandValidationType: 'USSD',
        savedEntityTillOutOfBandValidation: null,
        rememberMeEntityInfo: null,
        mobileBiometric: false,
        sessionTimer: null,
        localExpiryDate: null,
        localExpirySpan: null,
        dashboardCards: [],
        showPopupsList: false,
        popupsList: []
    }),
    getters: {
        getAllCustomerInfo: (state) => {
            if (!state.allUserInfo) return null
            return {
                ...state.allUserInfo,
                displayName: firstLastWordBuilder(state.allUserInfo.customer.name) || state.allUserInfo.customerInfo.msisdn
            }
        },
        getOutOfBandValidationType: (state) => state.outOfBandValidationType,
        getRememberMeEntityInfo: (state) => state.rememberMeEntityInfo,
        getMobileBiometric: (state) => state.mobileBiometric,
        getToken: (state) => state.userSession ? state.userSession.token : null,
        getSessionTime: (state) => state.userSession ? state.localExpirySpan : null,
        getSessionExpiryDate: (state) => state.userSession ? state.localExpiryDate : null,
        getSessionTimer: (state) => parseTimer(state.sessionTimer),
        getSessionExpiredState: (state) => state.sessionExpired,
        getSavedEntityTillOutOfBandValidation: (state) => state.savedEntityTillOutOfBandValidation,
        getAccountGroups: (state) => {
            let colorPicker = -1
            return state.allUserInfo.accountGroups.map(account => {
                colorPicker++
                if (colorPicker === 3) colorPicker = 0
                return {
                    ...account,
                    title: state.allUserInfo.contractName || state.allUserInfo.customer.username,
                    displayName: account.type === 'PARTICULAR' ? i18n.global.t('particularAccounts') : firstLastWordBuilder(account.name),
                    displayType: account.type === 'PARTICULAR' ? i18n.global.t('particularAccounts') : i18n.global.t('companyAccounts'),
                    acronym: acronymBuilder(state.allUserInfo.contractName || state.allUserInfo.customer.username),
                    color: ACCOUNT_GROUPS_COLLORS[colorPicker]
                }
            })
        },
        getDashboardCardsList: (state) => state.dashboardCards,
        getShowPopupsList: (state) => state.showPopupsList,
        getPopupsList: (state) => state.popupsList
    },
    actions: {
        async login(payload) {
            const res = await apiAuthentication.login(payload)
            if (res) {
                const data = res.data.data
                this._saveUserInfo({ ...data, isCompany: payload.isCompany })
                this.outOfBandValidationType = payload.outOfBandValidationType
                this._updateRemainingSeconds(data.session.remainingSecondsForSessionExpire)
            }
            return res
        },
        async enrollBiometricLogin(payload) {
            const res = await apiAuthentication.biometricLogin(payload)
            if (res) {
                const data = res.data.data
                this._saveUserInfo({ ...data, isCompany: payload.isCompany })
                this.outOfBandValidationType = payload.outOfBandValidationType
                this._updateRemainingSeconds(data.session.remainingSecondsForSessionExpire)
            }
            return res
        },
        async loginRememberMe(payload) {
            const res = await apiAuthentication.loginRememberMe(payload)
            if (res) {
                const data = res.data.data
                this._saveUserInfo({ ...data, isCompany: payload.isCompany })
                this._updateRemainingSeconds(data.session.remainingSecondsForSessionExpire)
            }
            return res
        },
        async invalidRememberMe(payload) {
            const res = await apiAuthentication.invalidRememberMe(payload)
            removeItemFromSessionStorage(USER_SESSION)
            window.localStorage.clear()
            window.location.reload(true)
            return res
        },
        async logout() {
            const res = await apiAuthentication.logout()
            removeItemFromSessionStorage(USER_SESSION)
            window.location.reload(true)
            return res
        },
        forceLogout() {
            removeItemFromSessionStorage(USER_SESSION)
            window.location.reload(true)
            if (window.StatusBar) window.StatusBar.styleLightContent()
        },
        async fetchBiometricChallenge(payload) {
            return await apiAuthentication.getBiometricChallenge(payload)
        },
        updateMobileBiometric(payload) {
            this.mobileBiometric = payload
        },
        async sendLoginBiometric(payload) {
            const res = await apiAuthentication.sendLoginBiometricChallenge(payload)
            if (res) {
                const data = res.data.data
                this._saveUserInfo(data)
                this._updateRemainingSeconds(data.session.remainingSecondsForSessionExpire)
            }
            return res
        },
        updateSessionTimer(payload) {
            this.sessionTimer = payload
            if (payload < 1) this.sessionExpired = true
        },
        async extendSession(payload) {
            const res = await apiAuthentication.extendSession(payload)
            if (res) this._updateRemainingSeconds(res.data.data.remainingSecondsForSessionExpire)
            return res
        },
        async sendExtensionBiometricChallenge(payload) {
            const res = await apiAuthentication.sendExtensionBiometricChallenge(payload)
            if (res) this._updateRemainingSeconds(res.data.remainingSecondsForSessionExpire)
            return res
        },
        updateSessionExpireState(payload) {
            this.sessionExpired = payload
        },
        updateRemainingSecondsForSessionExpire(payload) {
            this._updateRemainingSeconds(payload)
        },
        async updateEntityRememberMeInfo(payload) {
            if (this.rememberMeEntityInfo && this.rememberMeEntityInfo.entity !== payload.entity) {
                return Promise.resolve({ errorLevel: 'alert', errorCode: 'entity_not_match_saved' })
            }
            this._updateEntityRememberMe(payload)
            return Promise.resolve({ errorLevel: 'info' })
        },
        updateSelectedEntityRememberInfo(payload) {
            this.rememberMeEntityInfo.listOfAcessions.map(el => {
                el.selected = el.acessionNumber === payload
            })
        },
        updateEntityRememberInfo(payload) {
            this.rememberMeEntityInfo = payload
            const key = 'bai-remember-me-entity-info'
            if (window.cordova) {
                var sharedPreferences = window.plugins.SharedPreferences.getInstance()
                sharedPreferences.put(key, JSON.stringify(this.rememberMeEntityInfo))
            } else {
                window.localStorage.setItem(key, JSON.stringify(this.rememberMeEntityInfo))
            }
        },
        saveThemeInUserRememberMeSession(payload) {
            if (!this.rememberMeEntityInfo) return
            this.rememberMeEntityInfo.listOfAcessions = this.rememberMeEntityInfo.listOfAcessions.map(el => {
                return { ...el, selectedAppTheme: payload }
            })
            if (!this.rememberMeEntityInfo.listOfAcessions.length) this.rememberMeEntityInfo = null
            const key = 'bai-remember-me-entity-info'
            if (window.cordova) {
                var sharedPreferences = window.plugins.SharedPreferences.getInstance()
                sharedPreferences.put(key, JSON.stringify(this.rememberMeEntityInfo))
            } else {
                window.localStorage.setItem(key, JSON.stringify(this.rememberMeEntityInfo))
            }
        },
        removeAcessionFromEntityRememberMeInfo(payload) {
            this.rememberMeEntityInfo.listOfAcessions = this.rememberMeEntityInfo.listOfAcessions.filter(obj => obj.acessionNumber !== payload)
            if (!this.rememberMeEntityInfo.listOfAcessions.length) this.rememberMeEntityInfo = null
            const key = 'bai-remember-me-entity-info'
            if (window.cordova) {
                var sharedPreferences = window.plugins.SharedPreferences.getInstance()
                sharedPreferences.put(key, JSON.stringify(this.rememberMeEntityInfo))
            } else {
                window.localStorage.setItem(key, JSON.stringify(this.rememberMeEntityInfo))
            }
        },
        async changeAccountGroupOfSession(payload) {
            const res = await apiAuthentication.setSessionContractAccountGroup(payload)
            if (res) {
                /* Keep isCompany in sync with the newly selected default group so the flag never
                   desyncs from accountGroups.find(default).type after an in-app group switch. */
                const selectedAccountGroup = this.allUserInfo.accountGroups.find(account => account.id === payload.data.groupId)
                this.allUserInfo = {
                    ...this.allUserInfo,
                    isCompany: selectedAccountGroup ? selectedAccountGroup.type === 'COMPANY' : this.allUserInfo.isCompany,
                    userDefaultPreferencePickedForSession: true,
                    accountGroups: this.allUserInfo.accountGroups.map(account => ({
                        ...account,
                        default: account.id === payload.data.groupId
                    }))
                }
                if (!window.cordova) addItemToSessionStorage(USER_SESSION, this.allUserInfo)
            }
            return res
        },
        async getDashboardCards(payload) {
            if (this.dashboardCards.length) return
            const res = await apiDashboardCard.getDashboardCards(payload)
            if (res) this.dashboardCards = res.data.data.dashboardCards
            return res
        },
        async getMerchantNameNonAuthenticated(payload) {
            return await apiMerchant.getMerchantNameNonAuthenticated(payload)
        },
        async requestPopupsList() {
            const res = await apiPopup.requestPopupsList()
            if (res) {
                const popUps = res.data.data.popUps
                if (popUps.length) this.showPopupsList = true
                this.popupsList = popUps
            }
            return res
        },
        async requestDismissPopups(payload) {
            const res = await apiPopup.dismissPopups(payload)
            if (res) {
                this.showPopupsList = false
                this.popupsList = []
            }
            return res
        },
        clearPopUpList() {
            this.showPopupsList = false
            this.popupsList = []
        },
        updateCustomerImageUrl(payload) {
            this.allUserInfo.customer.avatarImageUrl = payload
            if (this.rememberMeEntityInfo) {
                this.rememberMeEntityInfo.listOfAcessions = this.rememberMeEntityInfo.listOfAcessions.map(acession => ({
                    ...acession,
                    avatarImageUrl: acession.acessionNumber === this.allUserInfo.acession ? this.allUserInfo.customer.avatarImageUrl : acession.avatarImageUrl
                }))
                if (!window.cordova) addItemToSessionStorage(USER_SESSION, this.allUserInfo)
                const key = 'bai-remember-me-entity-info'
                if (window.cordova) {
                    var sharedPreferences = window.plugins.SharedPreferences.getInstance()
                    sharedPreferences.put(key, JSON.stringify(this.rememberMeEntityInfo))
                } else {
                    window.localStorage.setItem(key, JSON.stringify(this.rememberMeEntityInfo))
                }
            }
        },
        async getOutofBandValidationRequestStatus() {
            const res = await apiAuthentication.getOutofBandValidationRequestStatus()
            if (res) {
                const userData = this.allUserInfo
                if (userData) {
                    this.allUserInfo = {
                        ...this.allUserInfo,
                        requiresOutOfBandSessionAuthentication: res.data.data.status !== 'VALID'
                    }
                    if (!window.cordova) window.sessionStorage.setItem(USER_SESSION, JSON.stringify(this.allUserInfo))
                }
            }
            return res
        },
        async retryOutofbandValidationRequest() {
            return await apiAuthentication.retryOutofbandValidationRequest()
        },
        async getOutofbandRequestDetail(payload) {
            return await apiAuthentication.getOutofbandRequestDetail(payload)
        },
        async validateRefuseOutofbandValidationRequest(payload) {
            return await apiAuthentication.validateRefuseOutofbandValidationRequest(payload)
        },
        async updateDefaultAccountsGroup(payload) {
            const res = await apiAccounts.accountsGroupDefault(payload)
            if (res) this._updateInitialActions('CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP')
            return res
        },
        async updateAccountGroupInitialAction() {
            const res = await apiAuthentication.updateAccountGroupInitialAction()
            if (res) this._updateInitialActions('CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP')
            return res
        },
        updateSessionType() {
            const userData = this.allUserInfo
            if (userData) {
                this.allUserInfo = { ...this.allUserInfo, isCompany: !this.allUserInfo.isCompany }
                if (!window.cordova) window.sessionStorage.setItem(USER_SESSION, JSON.stringify(this.allUserInfo))
            }
        },
        async modifyPassword(payload) {
            return await apiAuthentication.modifyPassword(payload)
        },
        async changeUserFriendlyName(payload) {
            return await apiCustomers.changeUserFriendlyName(payload)
        },
        updateInitialActions(payload) {
            this._updateInitialActions(payload)
        },
        /* Internal helpers */
        _saveUserInfo(payload) {
            const userDefaultPreferencePickedForSession = payload.accountGroups.find(obj => obj.userDefaultPreference) ||
                payload.accountGroups.filter(obj => obj.type === 'COMPANY').length === 1

            /* Hybrid app: there is no company/particular login selector, so the isCompany flag
               coming from the login form is unreliable (always false). Derive it from the account
               group MDW marks as default — the same source Home/Menu/App.vue already use. When no
               default is marked yet (a company user who must still pick a group), fall back to
               whether any company group exists so the "choose group" step still triggers. */
            const defaultAccountGroup = payload.accountGroups.find(obj => obj.default)
            const isCompany = defaultAccountGroup
                ? defaultAccountGroup.type === 'COMPANY'
                : payload.accountGroups.some(obj => obj.type === 'COMPANY')

            /* `acession` must be set HERE, not by each caller. It is how the logged-in user is
               matched against the saved remember-me list (ExpiredSession, HomeMobile,
               AssociationInformation, updateCustomerImageUrl all do
               `listOfAcessions.find(el => el.acessionNumber === allUserInfo.acession)`).
               Only `login` and `enrollBiometricLogin` used to pass it, so after a remember-me
               login — keypad (`loginRememberMe`) or biometric (`sendLoginBiometric`) — it was
               undefined, the find() returned undefined and every `?.hasBiometricEnrolled` gate
               read as false: the fingerprint button silently disappeared from the expired-session
               modal for exactly the users who log in with biometrics. */
            this.allUserInfo = {
                ...payload,
                isCompany,
                acession: payload.customer.username,
                acessionNumber: payload.customer.username,
                userDefaultPreferencePickedForSession
            }
            this.userSession = payload.session
            if (!window.cordova) addItemToSessionStorage(USER_SESSION, this.allUserInfo)
        },
        _updateRemainingSeconds(remainingSeconds) {
            const currentTime = new Date().getTime()
            const expiryDate = new Date()
            expiryDate.setTime(currentTime + (remainingSeconds || this.userSession.remainingSecondsForSessionExpire) * 1000)
            this.localExpiryDate = expiryDate.getTime()
            this.localExpirySpan = (this.localExpiryDate - currentTime) / 1000
        },
        _updateEntityRememberMe(payload) {
            if (payload.requiresOutOfBandSessionAuthentication) {
                this.savedEntityTillOutOfBandValidation = payload
                return
            }
            this.savedEntityTillOutOfBandValidation = null
            if (!this.rememberMeEntityInfo) {
                this.rememberMeEntityInfo = {
                    entity: payload.entity,
                    entityName: payload.entityName,
                    listOfAcessions: [{ ...payload.acession }]
                }
            } else {
                const savedAcessionIndex = this.rememberMeEntityInfo.listOfAcessions.findIndex(obj => obj.acessionNumber === payload.acession.acessionNumber)
                if (savedAcessionIndex === -1) {
                    this.rememberMeEntityInfo.listOfAcessions.push(payload.acession)
                } else {
                    /* An already-saved accession must be MERGED, not skipped. Skipping meant a later
                       successful biometric enrollment (and every refreshed rememberMeToken) was thrown
                       away, so an accession first saved without biometrics - e.g. because the user
                       cancelled the Face ID prompt on the first login - could never show the biometric
                       button again. Undefined values are dropped so a login that did not enroll
                       biometrics does not wipe an existing hasBiometricEnrolled, and locally-owned
                       fields (selected, selectedAppTheme, hasSeenSecureAssociationInitialAction) are
                       preserved because they are not part of the login payload. */
                    const mergedAcession = { ...this.rememberMeEntityInfo.listOfAcessions[savedAcessionIndex] }
                    Object.keys(payload.acession).forEach(field => {
                        if (payload.acession[field] !== undefined) mergedAcession[field] = payload.acession[field]
                    })
                    this.rememberMeEntityInfo.listOfAcessions[savedAcessionIndex] = mergedAcession
                }
            }
            const key = 'bai-remember-me-entity-info'
            if (window.cordova) {
                var sharedPreferences = window.plugins.SharedPreferences.getInstance()
                sharedPreferences.put(key, JSON.stringify(this.rememberMeEntityInfo))
            } else {
                window.localStorage.setItem(key, JSON.stringify(this.rememberMeEntityInfo))
            }
        },
        _updateInitialActions(payload) {
            const userData = this.allUserInfo
            if (userData) {
                this.allUserInfo = {
                    ...this.allUserInfo,
                    initialActions: this.allUserInfo.initialActions.filter(el => el.type !== payload)
                }
                if (!window.cordova) window.sessionStorage.setItem(USER_SESSION, JSON.stringify(this.allUserInfo))
            }
        },
        async sendMigrationCredentials(payload) {
            return await apiAuthentication.sendMigrationCredentials(payload)
        }
    }
})
